import {State, Action, Selector, StateContext} from '@ngxs/store';
import {CreateShortLink, GetInterestRate} from './utility.actions';
import {Injectable} from '@angular/core';
import {UtilitiesService} from '@shared/service/utilities/utilities.service';
import {InterestRateData, ServerResponse} from '@shared/interfaces';
import {LoggerService} from '@app/service/logger/logger.service';
import {checkNested} from '@shared/helpers';

export interface UtilityStateModel {
  shortLinks: { [k: string]: string };
  interestRates: InterestRateData;
}

@State<UtilityStateModel>({
  name: 'utility',
  defaults: {
    shortLinks: {},
    interestRates: {}
  }
})
@Injectable()
export class UtilityState {
  constructor(private utilityService: UtilitiesService, private logger: LoggerService) {
  }

  @Selector()
  static getShortLinks(state: UtilityStateModel) {
    return state.shortLinks;
  }

  @Selector()
  static getInterestRates(state: UtilityStateModel) {
    return state.interestRates;
  }

  @Action(CreateShortLink)
  createShortLink(sc: StateContext<UtilityStateModel>, {payload}: CreateShortLink) {
    const url = payload.url;
    let shortLinks = sc.getState().shortLinks;
    if (shortLinks[url]) {
      return Promise.resolve(shortLinks[url]);
    }
    return this.utilityService.createShortLink(url)
      .then((result: ServerResponse) => {
        if (shortLinks) {
          shortLinks = Object.assign({}, shortLinks, {[url]: result.data.shortLink});
        }
        return sc.patchState({
          shortLinks
        });
      })
      .catch(error => {
        return this.logger.error(`An error occurred while trying to create short link - ${error}`);
      });
  }

  @Action(GetInterestRate)
  getInterestRate(sc: StateContext<UtilityStateModel>, {payload}: GetInterestRate) {
    const state = payload.state;
    const creditType = payload.creditType;
    const carCondition = payload.carCondition;

    const interestRates = sc.getState().interestRates;
    if (checkNested(interestRates, state, creditType, carCondition)) {
      return Promise.resolve(true);
    }
    return this.utilityService.getInterestRate(state, creditType, carCondition)
      .then((result: number) => {
        const curInterestRates = sc.getState().interestRates;
        const newInterestRates = JSON.parse(JSON.stringify(curInterestRates));
        if (!checkNested(newInterestRates, state)) {
          newInterestRates[state] = {};
        }
        if (!checkNested(newInterestRates, state, creditType)) {
          newInterestRates[state][creditType] = {};
        }
        newInterestRates[state][creditType][carCondition] = result;
        return sc.patchState({
          interestRates: newInterestRates
        });
      })
      .catch(error => {
        return this.logger.error(`An error occurred while trying to get interest rate - ${error}`);
      });
  }
}
